import { sortDesc } from "./util";
import { Unit } from "./units";

interface Tree {
  [key: number]: {
    [key: number]: string
  }
}

export default class UnitSelector {
  tree: Tree;
  units: Unit[];

  constructor(units: Unit[]) {
    this.units = units;
    this.tree = UnitSelector.buildTree(units);
  }

  static buildTree(units: Unit[]) {
    const tree: Tree = {};

    units.forEach((unit) => {
      const { key, x, y } = unit;

      if (!key) {
        return;
      }

      if (!tree[x]) {
        tree[x] = {
          [y]: key,
        };
      } else {
        tree[x][y] = key;
      }
    });

    return tree;
  }

  static viableSizes(max: number, options: number[] = []): number[] {
    return options
      .filter(opt => opt <= max)
      .sort(sortDesc);
  }

  bestFit(x: number, y: number): Unit {
    const { tree } = this;

    if (tree[x] && tree[x][y]) {
      return {
        x,
        y,
        key: tree[x][y],
      };
    }

    const xKeys = Object.keys(tree).map(Number);
    const xCans = UnitSelector.viableSizes(x, xKeys);
    const valid: any = {};

    xCans.forEach((xCan) => {
      const yKeys = Object.keys(tree[xCan]).map(Number);
      const yCans = UnitSelector.viableSizes(y, yKeys);

      if (yCans.length) {
        valid[xCan] = yCans;
      }
    });

    if (!Object.keys(valid).length) {
      return { x: 0, y: 0, key: null } as Unit;
    }

    const [bestX, bestY]: any = Object.keys(valid)
      .map(Number)
      .sort(sortDesc)
      .map(key => ([key, valid[key].shift()]))
      .shift();

    return {
      x: bestX,
      y: bestY,
      key: tree[bestX][bestY],
    };
  }

  lookup(unitKey: string): string | null {
    const unit = this.units.find(({ key }) => key === unitKey);

    return unit?.id || null;
  }
}
